import React from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import axios from "axios";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";

import { APP_API_V1_BASE } from "../../../config/api";

const COLOR_PALETTE = [
  "#2563EB",
  "#0F766E",
  "#F59E0B",
  "#DC2626",
  "#7C3AED",
  "#0891B2",
  "#EA580C",
  "#16A34A",
];

const FIXED_COLORS = {
  temp: "#DC2626",
  temperature: "#DC2626",
  moisture: "#2563EB",
  humidity: "#0F766E",
  voltage: "#7C3AED",
  raw_line_voltage: "#2563EB",
};

const TIME_OPTIONS = [
  { label: "Last 1h", value: "1h" },
  { label: "Last 6h", value: "6h" },
  { label: "Last 24h", value: "24h" },
  { label: "Last 7d", value: "7d" },
  { label: "Custom", value: "custom" },
];

const AGGREGATION_OPTIONS = [
  { label: "Raw", value: "raw" },
  { label: "Average", value: "avg" },
  { label: "Minimum", value: "min" },
  { label: "Maximum", value: "max" },
];

const INTERVAL_OPTIONS = [
  { label: "Auto", value: "auto" },
  { label: "1 minute", value: "1m" },
  { label: "5 minutes", value: "5m" },
  { label: "15 minutes", value: "15m" },
  { label: "1 hour", value: "1h" },
];

const GRAPH_OPTIONS = [
  { label: "Line", value: "line" },
  { label: "Area", value: "area" },
  { label: "Bar", value: "bar" },
];

const injectAssets = () => {
  if (document.getElementById("analytics-console-assets")) return;

  const link = document.createElement("link");
  link.id = "analytics-console-assets";
  link.rel = "stylesheet";
  link.href =
    "https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800;900&family=JetBrains+Mono:wght@400;500;600;700&display=swap";
  document.head.appendChild(link);
};

const normalizeKey = (key) => String(key || "").toLowerCase();

const getMetricColor = (key, index = 0) => {
  const normalized = normalizeKey(key);
  if (FIXED_COLORS[normalized]) return FIXED_COLORS[normalized];
  for (const [token, color] of Object.entries(FIXED_COLORS)) {
    if (normalized.includes(token)) return color;
  }
  return COLOR_PALETTE[index % COLOR_PALETTE.length];
};

const unitOf = (field) => {
  if (!field) return "";
  if (field.unit === "Custom") return field.customUnit || "";
  return field.unit || "";
};

const fieldLabel = (field) =>
  field?.label || field?.name || field?.key || "Metric";

const formatNumber = (value, fallback = "-") => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return fallback;
  return new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: Math.abs(numeric) >= 100 ? 1 : 2,
  }).format(numeric);
};

const formatDateTime = (value, format = "DD MMM YYYY, hh:mm:ss A") => {
  if (!value) return "-";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format(format) : "-";
};

const timestampFormatForRange = (range) =>
  range === "7d" ? "DD MMM HH:mm" : "HH:mm:ss";

const normalizeFieldType = (field) =>
  String(field?.type || field?.data_type || "").toLowerCase();

const isChartableField = (field) => {
  const type = normalizeFieldType(field);
  return !type || ["number", "float", "double", "integer", "numeric"].includes(type);
};

const getStats = (rows) => {
  const values = rows
    .map((row) => Number(row.value))
    .filter((value) => Number.isFinite(value));

  if (!values.length) return null;

  const min = Math.min(...values);
  const max = Math.max(...values);
  const latest = values[values.length - 1];
  const avg = values.reduce((sum, value) => sum + value, 0) / values.length;

  return { min, max, avg, latest, count: values.length };
};

const normalizePercent = (value, min, max) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  if (min === max) return 50;
  return Number((((numeric - min) / (max - min)) * 100).toFixed(2));
};

const panelSx = {
  borderRadius: 5,
  border: "1px solid rgba(15,23,42,0.08)",
  backgroundColor: "#fff",
  boxShadow: "0 24px 70px rgba(15, 23, 42, 0.08)",
  overflow: "hidden",
};

const cardSx = {
  borderRadius: 4,
  border: "1px solid rgba(15,23,42,0.08)",
  backgroundColor: "#fff",
  boxShadow: "0 14px 34px rgba(15, 23, 42, 0.06)",
};

function ControlSelect({ label, value, onChange, options }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        sx={{
          color: "#CBD5E1",
          fontSize: 11,
          fontWeight: 900,
          letterSpacing: "0.12em",
          mb: 0.75,
          textTransform: "uppercase",
        }}
      >
        {label}
      </Typography>
      <Select
        size="small"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        fullWidth
        sx={{
          height: 42,
          borderRadius: 2.5,
          backgroundColor: "rgba(255,255,255,0.96)",
          color: "#0F172A",
          fontWeight: 700,
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(255,255,255,0.55)",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#67E8F9",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#22D3EE",
          },
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              borderRadius: 3,
              border: "1px solid rgba(15,23,42,0.08)",
              boxShadow: "0 18px 45px rgba(15,23,42,0.18)",
            },
          },
        }}
      >
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
}

function EmptyState({ title, message }) {
  return (
    <Box
      sx={{
        minHeight: 300,
        display: "grid",
        placeItems: "center",
        px: 3,
        textAlign: "center",
        background:
          "linear-gradient(135deg, rgba(248,250,252,0.92), rgba(236,253,245,0.45))",
      }}
    >
      <Box>
        <Typography sx={{ fontSize: 20, fontWeight: 900, color: "#0F172A" }}>
          {title}
        </Typography>
        <Typography sx={{ mt: 1, color: "#64748B", maxWidth: 520 }}>
          {message}
        </Typography>
      </Box>
    </Box>
  );
}

function AnalyticsTooltip({ active, payload, label, selectedField, metricMap }) {
  if (!active || !payload?.length) return null;

  const rawTime = payload[0]?.payload?.ts || label;
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 3,
        backgroundColor: "rgba(15,23,42,0.96)",
        color: "#fff",
        boxShadow: "0 20px 50px rgba(15,23,42,0.28)",
        border: "1px solid rgba(255,255,255,0.12)",
        minWidth: 190,
      }}
    >
      <Typography
        sx={{
          fontFamily: "JetBrains Mono, monospace",
          fontSize: 11,
          color: "#BAE6FD",
          mb: 1,
        }}
      >
        {formatDateTime(rawTime)}
      </Typography>
      <Stack spacing={0.75}>
        {payload.map((item) => {
          const field = metricMap?.get(item.dataKey) || selectedField;
          const unit = unitOf(field);
          return (
            <Stack
              key={item.dataKey}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={2}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box
                  sx={{
                    width: 9,
                    height: 9,
                    borderRadius: "50%",
                    backgroundColor: item.color,
                  }}
                />
                <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
                  {fieldLabel(field)}
                </Typography>
              </Stack>
              <Typography
                sx={{
                  fontFamily: "JetBrains Mono, monospace",
                  fontSize: 13,
                  fontWeight: 800,
                }}
              >
                {formatNumber(item.value)}
                {unit ? ` ${unit}` : ""}
              </Typography>
            </Stack>
          );
        })}
      </Stack>
    </Box>
  );
}

function MetricStat({ label, value, unit, highlight }) {
  return (
    <Box
      sx={{
        flex: "1 1 120px",
        p: 1.5,
        borderRadius: 3,
        border: highlight
          ? "1px solid rgba(37,99,235,0.22)"
          : "1px solid rgba(15,23,42,0.08)",
        backgroundColor: highlight ? "#EFF6FF" : "#F8FAFC",
      }}
    >
      <Typography
        sx={{
          color: highlight ? "#1D4ED8" : "#64748B",
          fontSize: 11,
          fontWeight: 900,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          color: "#0F172A",
          fontSize: 24,
          fontWeight: 900,
          mt: 0.5,
          fontFamily: "JetBrains Mono, monospace",
        }}
      >
        {value}
        {unit ? (
          <Typography component="span" sx={{ ml: 0.5, fontSize: 13, color: "#64748B" }}>
            {unit}
          </Typography>
        ) : null}
      </Typography>
    </Box>
  );
}

export default function AnalyticsModel({ device }) {
  React.useEffect(() => {
    injectAssets();
  }, []);

  const [timeRange, setTimeRange] = React.useState("24h");
  const [aggregation, setAggregation] = React.useState("avg");
  const [interval, setIntervalVal] = React.useState("auto");
  const [graphType, setGraphType] = React.useState("line");
  const [selectedParams, setSelectedParams] = React.useState([]);
  const [compareParams, setCompareParams] = React.useState([]);
  const [showCompare, setShowCompare] = React.useState(false);
  const [showAllCompare, setShowAllCompare] = React.useState(false);
  const [customStart, setCustomStart] = React.useState(
    dayjs().subtract(1, "hour"),
  );
  const [customEnd, setCustomEnd] = React.useState(dayjs());
  const [telemetryData, setTelemetryData] = React.useState({});
  const [seriesMeta, setSeriesMeta] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");

  const schema = React.useMemo(
    () => (Array.isArray(device?.schema) ? device.schema.filter((item) => item?.key) : []),
    [device?.schema],
  );

  const metricMap = React.useMemo(() => {
    const map = new Map();
    schema.forEach((field) => map.set(field.key, field));
    return map;
  }, [schema]);

  const chartableSchema = React.useMemo(
    () => schema.filter(isChartableField),
    [schema],
  );

  React.useEffect(() => {
    if (!chartableSchema.length) {
      setSelectedParams([]);
      return;
    }

    setSelectedParams((prev) => {
      const current = prev[0];
      if (current && chartableSchema.some((field) => field.key === current)) {
        return prev;
      }
      return [chartableSchema[0].key];
    });
  }, [chartableSchema]);

  React.useEffect(() => {
    setCompareParams((prev) =>
      prev.filter((key) => chartableSchema.some((field) => field.key === key)),
    );
  }, [chartableSchema]);

  const { from, to } = React.useMemo(() => {
    const now = dayjs();
    if (timeRange === "custom") return { from: customStart, to: customEnd };

    const rangeMap = {
      "1h": ["hour", 1],
      "6h": ["hour", 6],
      "24h": ["hour", 24],
      "7d": ["day", 7],
    };
    const [unit, value] = rangeMap[timeRange] || ["hour", 24];
    return { from: now.subtract(value, unit), to: now };
  }, [timeRange, customStart, customEnd]);

  const effectiveInterval = React.useMemo(() => {
    if (interval !== "auto") return interval;
    return (
      {
        "1h": "1m",
        "6h": "5m",
        "24h": "5m",
        "7d": "1h",
      }[timeRange] || "5m"
    );
  }, [interval, timeRange]);

  const selectedKey = selectedParams[0] || "";
  const selectedField = metricMap.get(selectedKey) || null;
  const selectedUnit = unitOf(selectedField);
  const selectedColor = getMetricColor(selectedKey);

  React.useEffect(() => {
    if (!device?.id || !selectedParams.length || !from || !to) {
      setTelemetryData({});
      setSeriesMeta(null);
      return;
    }

    const controller = new AbortController();

    const run = async () => {
      try {
        setLoading(true);
        setError("");
        const token = localStorage.getItem("authToken");
        if (!token) return;

        const keys = [...new Set([...selectedParams, ...compareParams])]
          .filter(Boolean)
          .join(",");

        const res = await axios.get(
          `${APP_API_V1_BASE}/devices/${device.id}/telemetry/series`,
          {
            signal: controller.signal,
            headers: { Authorization: `Bearer ${token}` },
            params: {
              keys,
              from: from.toISOString(),
              to: to.toISOString(),
              agg: aggregation,
              interval: effectiveInterval,
            },
          },
        );

        setTelemetryData(res.data?.series || {});
        setSeriesMeta(res.data?.meta || null);
      } catch (err) {
        if (axios.isCancel?.(err) || err?.name === "CanceledError") return;
        setTelemetryData({});
        setSeriesMeta(null);
        setError(
          err?.response?.data?.error ||
            "Failed to load telemetry analytics for this time range.",
        );
      } finally {
        setLoading(false);
      }
    };

    run();

    return () => controller.abort();
  }, [
    device?.id,
    selectedParams,
    compareParams,
    from,
    to,
    aggregation,
    effectiveInterval,
  ]);

  const selectedSeries = telemetryData[selectedKey] || { points: [], events: [] };

  const chartData = React.useMemo(() => {
    const timestampFormat = timestampFormatForRange(timeRange);
    return (selectedSeries.points || [])
      .map((point) => ({
        ts: point.ts,
        time: formatDateTime(point.ts, timestampFormat),
        value: Number(point.value),
      }))
      .filter((point) => Number.isFinite(point.value))
      .sort((a, b) => dayjs(a.ts).valueOf() - dayjs(b.ts).valueOf());
  }, [selectedSeries.points, timeRange]);

  const stats = React.useMemo(() => getStats(chartData), [chartData]);

  const compareChartData = React.useMemo(() => {
    if (compareParams.length < 2) return [];

    const ranges = {};
    compareParams.forEach((key) => {
      const values = (telemetryData[key]?.points || [])
        .map((point) => Number(point.value))
        .filter((value) => Number.isFinite(value));
      ranges[key] = {
        min: values.length ? Math.min(...values) : 0,
        max: values.length ? Math.max(...values) : 0,
      };
    });

    const rowsByTs = new Map();
    compareParams.forEach((key) => {
      (telemetryData[key]?.points || []).forEach((point) => {
        const ts = point.ts;
        const row =
          rowsByTs.get(ts) ||
          {
            ts,
            time: formatDateTime(ts, timestampFormatForRange(timeRange)),
          };
        row[key] = normalizePercent(
          point.value,
          ranges[key].min,
          ranges[key].max,
        );
        rowsByTs.set(ts, row);
      });
    });

    return [...rowsByTs.values()].sort(
      (a, b) => dayjs(a.ts).valueOf() - dayjs(b.ts).valueOf(),
    );
  }, [compareParams, telemetryData, timeRange]);

  const compareSchema = showAllCompare
    ? chartableSchema
    : chartableSchema.slice(0, 6);

  const rangeLabel =
    timeRange === "custom"
      ? `${from.format("DD MMM, hh:mm A")} to ${to.format("DD MMM, hh:mm A")}`
      : TIME_OPTIONS.find((item) => item.value === timeRange)?.label || "Last 24h";

  const renderPrimaryChart = () => {
    if (!chartData.length) {
      return (
        <EmptyState
          title="No numeric data in this range"
          message={`No points were returned for ${fieldLabel(
            selectedField,
          )}. Try a wider time range, Raw aggregation, or another metric.`}
        />
      );
    }

    const commonProps = {
      data: chartData,
      margin: { top: 20, right: 28, left: 6, bottom: 16 },
    };

    const chartChildren = (
      <>
        <CartesianGrid strokeDasharray="4 6" stroke="#E2E8F0" vertical={false} />
        <XAxis
          dataKey="time"
          tick={{ fontSize: 11, fill: "#64748B", fontWeight: 700 }}
          tickLine={false}
          axisLine={{ stroke: "#CBD5E1" }}
          minTickGap={28}
        />
        <YAxis
          tick={{ fontSize: 11, fill: "#64748B", fontWeight: 700 }}
          tickLine={false}
          axisLine={false}
          width={58}
          tickFormatter={(value) => formatNumber(value)}
        />
        {stats ? (
          <ReferenceLine
            y={stats.avg}
            stroke="#94A3B8"
            strokeDasharray="6 6"
            label={{
              value: `Avg ${formatNumber(stats.avg)}`,
              position: "insideTopRight",
              fill: "#64748B",
              fontSize: 11,
              fontWeight: 800,
            }}
          />
        ) : null}
        <Tooltip
          content={
            <AnalyticsTooltip
              selectedField={selectedField}
              metricMap={metricMap}
            />
          }
        />
      </>
    );

    if (graphType === "bar") {
      return (
        <ResponsiveContainer width="100%" height={390}>
          <BarChart {...commonProps}>
            {chartChildren}
            <Bar
              dataKey="value"
              name={fieldLabel(selectedField)}
              fill={selectedColor}
              radius={[8, 8, 0, 0]}
              maxBarSize={34}
            />
          </BarChart>
        </ResponsiveContainer>
      );
    }

    if (graphType === "area") {
      return (
        <ResponsiveContainer width="100%" height={390}>
          <AreaChart {...commonProps}>
            <defs>
              <linearGradient id="primaryMetricFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={selectedColor} stopOpacity={0.28} />
                <stop offset="100%" stopColor={selectedColor} stopOpacity={0.02} />
              </linearGradient>
            </defs>
            {chartChildren}
            <Area
              type="monotone"
              dataKey="value"
              name={fieldLabel(selectedField)}
              stroke={selectedColor}
              strokeWidth={3}
              fill="url(#primaryMetricFill)"
              dot={chartData.length <= 24 ? { r: 4, strokeWidth: 2 } : false}
              activeDot={{ r: 7, strokeWidth: 2 }}
              connectNulls
            />
          </AreaChart>
        </ResponsiveContainer>
      );
    }

    return (
      <ResponsiveContainer width="100%" height={390}>
        <LineChart {...commonProps}>
          {chartChildren}
          <Line
            type="monotone"
            dataKey="value"
            name={fieldLabel(selectedField)}
            stroke={selectedColor}
            strokeWidth={3}
            dot={chartData.length <= 24 ? { r: 4, strokeWidth: 2 } : false}
            activeDot={{ r: 7, strokeWidth: 2 }}
            connectNulls
          />
        </LineChart>
      </ResponsiveContainer>
    );
  };

  if (!device) return null;

  if (!schema.length) {
    return (
      <Box sx={{ ...panelSx, p: 3 }}>
        <EmptyState
          title="No analytics schema configured"
          message="Promote discovered telemetry fields in the Device Type Studio before using analytics."
        />
      </Box>
    );
  }

  return (
    <Box sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <Box sx={panelSx}>
        <Box
          sx={{
            p: { xs: 2, md: 2.5 },
            background:
              "linear-gradient(135deg, rgba(15,23,42,0.98), rgba(15,118,110,0.9) 62%, rgba(8,47,73,0.96))",
          }}
        >
          <Stack
            direction={{ xs: "column", lg: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", lg: "center" }}
            spacing={2}
          >
            <Box>
              <Typography
                sx={{
                  color: "#A7F3D0",
                  fontSize: 12,
                  fontWeight: 900,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                }}
              >
                Analytics workspace
              </Typography>
              <Typography sx={{ color: "#fff", fontSize: 24, fontWeight: 900, mt: 0.5 }}>
                Signal trends
              </Typography>
              <Typography sx={{ color: "#CBD5E1", mt: 0.4, maxWidth: 660 }}>
                Inspect telemetry patterns, compare metrics, and validate field behavior
                without leaving the device record.
              </Typography>
            </Box>

            <Stack
              direction="row"
              spacing={1}
              useFlexGap
              flexWrap="wrap"
              sx={{ justifyContent: { xs: "flex-start", lg: "flex-end" } }}
            >
              <Chip
                label={rangeLabel}
                sx={{ color: "#0F172A", backgroundColor: "#E0F2FE", fontWeight: 900 }}
              />
              <Chip
                label={`${aggregation.toUpperCase()} / ${seriesMeta?.interval || effectiveInterval}`}
                sx={{ color: "#0F172A", backgroundColor: "#FEF3C7", fontWeight: 900 }}
              />
              <Chip
                label={`${stats?.count || 0} points`}
                sx={{ color: "#0F172A", backgroundColor: "#DCFCE7", fontWeight: 900 }}
              />
            </Stack>
          </Stack>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                xl: "repeat(4, minmax(0, 1fr))",
              },
              gap: 1.5,
              mt: 2.25,
            }}
          >
            <ControlSelect
              label="Time"
              value={timeRange}
              onChange={setTimeRange}
              options={TIME_OPTIONS}
            />
            <ControlSelect
              label="Aggregation"
              value={aggregation}
              onChange={setAggregation}
              options={AGGREGATION_OPTIONS}
            />
            <ControlSelect
              label="Interval"
              value={interval}
              onChange={setIntervalVal}
              options={INTERVAL_OPTIONS}
            />
            <ControlSelect
              label="Graph"
              value={graphType}
              onChange={setGraphType}
              options={GRAPH_OPTIONS}
            />
          </Box>

          {timeRange === "custom" ? (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1.5}
                sx={{ mt: 2 }}
              >
                <DateTimePicker
                  label="From"
                  value={customStart}
                  onChange={(value) => value && setCustomStart(value)}
                  maxDateTime={customEnd}
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                      sx: {
                        backgroundColor: "#fff",
                        borderRadius: 2,
                      },
                    },
                  }}
                />
                <DateTimePicker
                  label="To"
                  value={customEnd}
                  onChange={(value) => value && setCustomEnd(value)}
                  minDateTime={customStart}
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                      sx: {
                        backgroundColor: "#fff",
                        borderRadius: 2,
                      },
                    },
                  }}
                />
              </Stack>
            </LocalizationProvider>
          ) : null}
        </Box>

        <Box sx={{ p: { xs: 2, md: 3 }, backgroundColor: "#F8FAFC" }}>
          {error ? (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 3 }}>
              {error}
            </Alert>
          ) : null}

          <Box sx={{ ...cardSx, p: { xs: 2, md: 2.5 }, mb: 2.5 }}>
            <Stack
              direction={{ xs: "column", lg: "row" }}
              spacing={2}
              alignItems={{ xs: "stretch", lg: "center" }}
              justifyContent="space-between"
            >
              <Box sx={{ minWidth: 260 }}>
                <Typography
                  sx={{
                    color: "#64748B",
                    fontSize: 12,
                    fontWeight: 900,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    mb: 1,
                  }}
                >
                  Primary metric
                </Typography>
                <Select
                  value={selectedKey}
                  onChange={(event) => setSelectedParams([event.target.value])}
                  fullWidth
                  size="small"
                  sx={{
                    minWidth: { xs: "100%", md: 320 },
                    height: 48,
                    borderRadius: 3,
                    backgroundColor: "#fff",
                    fontWeight: 900,
                  }}
                >
                  {chartableSchema.map((field) => (
                    <MenuItem key={field.key} value={field.key}>
                      {fieldLabel(field)}
                      {unitOf(field) ? ` (${unitOf(field)})` : ""}
                    </MenuItem>
                  ))}
                </Select>
              </Box>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                flexWrap="wrap"
                sx={{ flex: 1 }}
              >
                <MetricStat
                  label="Latest"
                  value={formatNumber(stats?.latest)}
                  unit={selectedUnit}
                  highlight
                />
                <MetricStat label="Min" value={formatNumber(stats?.min)} unit={selectedUnit} />
                <MetricStat label="Average" value={formatNumber(stats?.avg)} unit={selectedUnit} />
                <MetricStat label="Max" value={formatNumber(stats?.max)} unit={selectedUnit} />
              </Stack>
            </Stack>
          </Box>

          <Box sx={{ ...cardSx, mb: 2.5 }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", md: "center" }}
              spacing={1.5}
              sx={{
                px: { xs: 2, md: 2.5 },
                py: 2,
                borderBottom: "1px solid rgba(15,23,42,0.08)",
              }}
            >
              <Box>
                <Typography sx={{ color: "#0F172A", fontSize: 20, fontWeight: 900 }}>
                  {fieldLabel(selectedField)}
                </Typography>
                <Typography sx={{ color: "#64748B", mt: 0.25 }}>
                  Metric key:{" "}
                  <Typography
                    component="span"
                    sx={{ fontFamily: "JetBrains Mono, monospace", fontWeight: 800 }}
                  >
                    {selectedKey}
                  </Typography>
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip label={graphType.toUpperCase()} sx={{ fontWeight: 900 }} />
                <Chip
                  label={selectedUnit || "No unit"}
                  sx={{ fontWeight: 900, backgroundColor: "#E0F2FE" }}
                />
                <Chip
                  label={formatDateTime(chartData[chartData.length - 1]?.ts)}
                  sx={{ fontWeight: 900, backgroundColor: "#ECFDF5" }}
                />
              </Stack>
            </Stack>

            <Box sx={{ position: "relative", p: { xs: 1, md: 2 } }}>
              {loading ? (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 2,
                    display: "grid",
                    placeItems: "center",
                    backgroundColor: "rgba(255,255,255,0.68)",
                    backdropFilter: "blur(2px)",
                  }}
                >
                  <CircularProgress />
                </Box>
              ) : null}
              {renderPrimaryChart()}
            </Box>
          </Box>

          <Box sx={{ ...cardSx }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", md: "center" }}
              spacing={1.5}
              sx={{
                px: { xs: 2, md: 2.5 },
                py: 2,
                borderBottom: "1px solid rgba(15,23,42,0.08)",
              }}
            >
              <Box>
                <Typography sx={{ color: "#0F172A", fontSize: 18, fontWeight: 900 }}>
                  Compare parameters
                </Typography>
                <Typography sx={{ color: "#64748B", mt: 0.25 }}>
                  Select two or more metrics. Values are normalized to 0-100%
                  so different engineering units can be compared.
                </Typography>
              </Box>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  disabled={compareParams.length < 2}
                  onClick={() => setShowCompare(true)}
                  sx={{
                    textTransform: "none",
                    fontWeight: 900,
                    borderRadius: 999,
                    px: 2.5,
                  }}
                >
                  Compare
                </Button>
                {showCompare ? (
                  <Button
                    onClick={() => {
                      setShowCompare(false);
                      setCompareParams([]);
                    }}
                    sx={{ textTransform: "none", fontWeight: 800 }}
                  >
                    Clear
                  </Button>
                ) : null}
              </Stack>
            </Stack>

            <Box sx={{ p: { xs: 2, md: 2.5 } }}>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {compareSchema.map((field, index) => {
                  const active = compareParams.includes(field.key);
                  const color = getMetricColor(field.key, index);
                  return (
                    <Box
                      key={field.key}
                      onClick={() => {
                        setShowCompare(false);
                        setCompareParams((prev) =>
                          active
                            ? prev.filter((key) => key !== field.key)
                            : [...prev, field.key],
                        );
                      }}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        px: 1.25,
                        py: 0.9,
                        borderRadius: 3,
                        border: `1px solid ${active ? color : "#CBD5E1"}`,
                        backgroundColor: active ? `${color}14` : "#fff",
                        cursor: "pointer",
                        transition: "0.16s ease",
                        "&:hover": {
                          borderColor: color,
                          backgroundColor: `${color}10`,
                        },
                      }}
                    >
                      <Checkbox
                        checked={active}
                        size="small"
                        onClick={(event) => event.stopPropagation()}
                        onChange={(event) => {
                          setShowCompare(false);
                          setCompareParams((prev) =>
                            event.target.checked
                              ? [...prev, field.key]
                              : prev.filter((key) => key !== field.key),
                          );
                        }}
                        sx={{
                          p: 0,
                          color: "#94A3B8",
                          "&.Mui-checked": { color },
                        }}
                      />
                      <Typography sx={{ fontWeight: 800, color: "#0F172A" }}>
                        {fieldLabel(field)}
                        {unitOf(field) ? ` (${unitOf(field)})` : ""}
                      </Typography>
                    </Box>
                  );
                })}
              </Stack>

              {chartableSchema.length > compareSchema.length ? (
                <Button
                  onClick={() => setShowAllCompare((prev) => !prev)}
                  sx={{ mt: 1.5, textTransform: "none", fontWeight: 800 }}
                >
                  {showAllCompare
                    ? "Show fewer metrics"
                    : `Show ${chartableSchema.length - compareSchema.length} more metrics`}
                </Button>
              ) : null}

              {compareParams.length < 2 ? (
                <Alert severity="info" sx={{ mt: 2, borderRadius: 3 }}>
                  Select at least two metrics to enable comparison.
                </Alert>
              ) : null}
            </Box>

            {showCompare ? (
              <>
                <Divider />
                <Box sx={{ p: { xs: 1, md: 2 } }}>
                  {compareChartData.length ? (
                    <ResponsiveContainer width="100%" height={340}>
                      <LineChart
                        data={compareChartData}
                        margin={{ top: 20, right: 28, left: 8, bottom: 14 }}
                      >
                        <CartesianGrid
                          strokeDasharray="4 6"
                          stroke="#E2E8F0"
                          vertical={false}
                        />
                        <XAxis
                          dataKey="time"
                          tick={{ fontSize: 11, fill: "#64748B", fontWeight: 700 }}
                          tickLine={false}
                          axisLine={{ stroke: "#CBD5E1" }}
                          minTickGap={28}
                        />
                        <YAxis
                          domain={[0, 100]}
                          tickFormatter={(value) => `${value}%`}
                          tick={{ fontSize: 11, fill: "#64748B", fontWeight: 700 }}
                          tickLine={false}
                          axisLine={false}
                          width={58}
                        />
                        <Tooltip
                          content={
                            <AnalyticsTooltip
                              selectedField={selectedField}
                              metricMap={metricMap}
                            />
                          }
                        />
                        <Legend
                          verticalAlign="top"
                          align="right"
                          wrapperStyle={{ paddingBottom: 12 }}
                        />
                        {compareParams.map((key, index) => (
                          <Line
                            key={key}
                            type="monotone"
                            dataKey={key}
                            name={fieldLabel(metricMap.get(key))}
                            stroke={getMetricColor(key, index)}
                            strokeWidth={2.5}
                            dot={compareChartData.length <= 24 ? { r: 3 } : false}
                            activeDot={{ r: 6 }}
                            connectNulls
                          />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <EmptyState
                      title="No comparison data"
                      message="The selected metrics do not have overlapping telemetry points in this time range."
                    />
                  )}
                </Box>
              </>
            ) : null}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
